import { useState, useEffect } from "react";
import { Modal } from 'react-bootstrap';
import { FaSearch, FaFilter, FaSyncAlt,FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { getAllSecurities } from "../../../service/api/SecurityService";

const AllSecuritiesModal = ({ show, onHide }) => {
  const itemsPerPage = 5;
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [securities, setSecurities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedMarket, setSelectedMarket] = useState("");

  // Types et marchés disponibles pour les filtres
  const securityTypes = ["Shares", "Bonds", "ETF", "Mutual Funds"];
  // const targetMarkets = ["Public", "Monetary", "Private", "Clear"];

  // Fonction pour charger les titres
  const fetchSecurities = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        type: selectedType || undefined,
      };

      const response = await getAllSecurities(params);
      
      setSecurities(response.items || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      toast.error("Erreur lors du chargement des titres");
      setSecurities([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Effet pour charger les données
  useEffect(() => {
    if (show) {
      fetchSecurities();
    }
  }, [show, currentPage, selectedType]);

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedType("");
    setSelectedMarket("");
    setCurrentPage(1);
  };

  // Filtrer les titres localement par recherche
  const filteredSecurities = securities.filter(security =>
    security.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    security.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    security.isin.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcul de la pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Formater les dates
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Formater le prix
  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price || 0);
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      dialogClassName="modal-dialog modal-dialog-centered modal-custom"
    >
      <Modal.Header className="bg-white text-black border-0">
        <Modal.Title className="fs-4 fw-bold">
          Liste Complète des Titres
        </Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-blue"
          onClick={onHide}
          aria-label="Close"
        ></button>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* En-tête avec filtres et recherche */}
        <div className="bg-light p-4 border-bottom">
          <div className="row g-3 align-items-end">
            {/* Recherche par texte */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-dark mb-2">
                <FaSearch className="me-2" />
                Rechercher un titre
              </label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Nom, symbole ou ISIN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filtre par type */}
            <div className="col-md-3">
              <label className="form-label fw-semibold text-dark mb-2">
                <FaFilter className="me-2" />
                Type de titre
              </label>
              <select
                className="form-select ps-5 py-2 border-0 bg-light rounded-3 shadow-sm w-100"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Tous les types</option>
                {securityTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Boutons d'action */}
            <div className="col-md-2">
              <button
                className="btn btn-outline-primary fw-bold shadow-sm px-3 py-2 rounded-3"
                onClick={handleResetFilters}
                data-tooltip-id="reset-tooltip"
              >
                <FaSyncAlt className="me-2" />
                Réinitialiser
              </button>
              <Tooltip id="reset-tooltip" place="top">
                Réinitialiser tous les filtres
              </Tooltip>
            </div>
          </div>

          {/* Statistiques */}
          <div className="row mt-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">
                  {totalCount > 0 ? `${totalCount} titre(s) trouvé(s)` : 'Aucun titre trouvé'}
                </span>
                {loading && (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des titres */}
        <div className="table-responsive" style={{ maxHeight: '60vh' }}>
          <table className="table table-striped align-middle">
            <thead className="bg-light sticky-top">
              <tr>
                <th scope="col" className="ps-4">Titre</th>
                <th scope="col">Symbole</th>
                <th scope="col">ISIN</th>
                <th scope="col">Type</th>
                <th scope="col">Marché</th>
                <th scope="col" className="text-end mr-2">Prix</th>
                {/* <th scope="col" className="text-end mr-2">Prix Final</th> */}
                {/* <th scope="col" className="text-center">Actions</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2 text-muted">Chargement des titres...</p>
                  </td>
                </tr>
              ) : filteredSecurities.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="text-muted">
                      <FaSearch size={48} className="mb-3 opacity-25" />
                      <h5 className="fw-semibold">Aucun titre trouvé</h5>
                      <p className="mb-0">
                        {searchTerm || selectedType || selectedMarket 
                          ? "Aucun résultat pour les critères sélectionnés." 
                          : "Aucun titre disponible dans le système."
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSecurities.map((security) => (
                  <tr key={security.id} className="transition-all">
                    <td className="ps-4">
                      <div>
                        <div className="fw-semibold text-dark">{security.name}</div>
                        <small className="text-muted">{security.issuerAccountName}</small>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary bg-opacity-10 text-white fs-6">
                        {security.symbol}
                      </span>
                    </td>
                    <td>
                      <code className="text-muted rounded-pill p-1">{security.isin}</code>
                    </td>
                    <td>
                      <span className={`badge ${
                        security.type === 'Shares' ? 'bg-success' : 
                        security.type === 'Bonds' ? 'bg-info' : 'bg-warning'
                      }`}>
                        {security.type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        security.targetMarket === 'Public' ? 'bg-primary' : 
                        security.targetMarket === 'Private' ? 'bg-secondary' : 'bg-dark'
                      }`}>
                        {security.targetMarket}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="fw-bold text-dark">{formatPrice(security.lastPrice)} FCFA</div>
                      <small className="text-muted">
                        Émission: {formatPrice(security.issuePrice)} FCFA
                      </small>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      {/* Pagination */}
      {/* Pagination recommandée */}
    {totalPages > 1 && (
        <Modal.Footer className="border-top bg-light">
            <div className="d-flex justify-content-between align-items-center w-100">
            <small className="text-muted">
                {totalCount} titre(s) au total
            </small>
            
            <nav>
                <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                    className="page-link d-flex align-items-center gap-1"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    >
                    <FaArrowLeft size={12} />
                    {/* <span>Précédent</span> */}
                    </button>
                </li>
                
                {/* Indicateur de page */}
                <li className="page-item">
                    <span className="page-link text-dark border-0">
                    <strong>{currentPage}</strong> / {totalPages}
                    </span>
                </li>

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                    className="page-link d-flex align-items-center gap-1"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    >
                    {/* <span>Suivant</span> */}
                    <FaArrowRight size={12} />
                    </button>
                </li>
                </ul>
            </nav>

            <small className="text-muted">
                {itemsPerPage} par page
            </small>
            </div>
        </Modal.Footer>
    )}
    </Modal>
  );
};

export default AllSecuritiesModal;